import { useRef, useState } from "react";
import { pad2 } from "./dateCore";

/** Is there any trailing digit that, appended to `next`, stays within range? */
function canExtend(next: string, min: number, max: number): boolean {
  for (let digit = 0; digit <= 9; digit += 1) {
    const candidate = Number.parseInt(next + digit, 10);
    if (candidate >= min && candidate <= max) return true;
  }
  return false;
}

export interface DigitSegmentOptions {
  min: number;
  max: number;
  value: number;
  onCommit: (value: number) => void;
  onAdvance?: () => void;
}

// Digit-only input; buffer keystrokes to build valid time values like 930 → 09:30, then clear on focus/blur and derive display from value.
export function useDigitSegment({ min, max, value, onCommit, onAdvance }: DigitSegmentOptions) {
  const [draft, setDraft] = useState<string | null>(null);
  const bufferRef = useRef("");
  const text = draft ?? pad2(value);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Tab" || event.key === "Shift") return;

    if (event.key === "Backspace") {
      event.preventDefault();
      bufferRef.current = "";
      setDraft("");
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const delta = event.key === "ArrowUp" ? 1 : -1;
      const span = max - min + 1;
      onCommit(((value - min + delta + span) % span) + min);
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    event.preventDefault();

    let next = bufferRef.current + event.key;
    let numeric = Number.parseInt(next, 10);
    // Overflowing restarts the buffer, so a second "9" after "19" begins "9".
    if (next.length > 2 || numeric > max) {
      next = event.key;
      numeric = Number.parseInt(next, 10);
    }
    bufferRef.current = next;
    setDraft(next);

    if (numeric >= min && numeric <= max) onCommit(numeric);

    if (next.length === 2 || !canExtend(next, min, max)) {
      bufferRef.current = "";
      onAdvance?.();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    bufferRef.current = "";
    setDraft(null);
    event.target.select();
  };

  const handleBlur = () => {
    bufferRef.current = "";
    setDraft(null);
  };

  return { text, handleKeyDown, handleFocus, handleBlur };
}
